import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, LOGO, openBooking } from "../../lib/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

const DesktopLink = ({ link, pathname }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = Array.isArray(link.children) && link.children.length > 0;
  const active =
    pathname === link.to ||
    (hasChildren && link.children.some((c) => pathname === c.to));

  if (!hasChildren) {
    return (
      <Link
        to={link.to}
        data-testid={`nav-link-${slug(link.label)}`}
        className={`text-[13px] tracking-wide transition-colors duration-300 relative group ${
          active ? "text-brand-ink" : "text-brand-ink/60 hover:text-brand-ink"
        }`}
      >
        {link.label}
        <span
          className={`absolute -bottom-1.5 left-0 h-px bg-brand-gold transition-all duration-300 ${
            active ? "w-full" : "w-0 group-hover:w-full"
          }`}
        />
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid={`nav-link-${slug(link.label)}`}
        onClick={() => setOpen((v) => !v)}
        className={`text-[13px] tracking-wide flex items-center gap-1 transition-colors duration-300 group ${
          active ? "text-brand-ink" : "text-brand-ink/60 hover:text-brand-ink"
        }`}
      >
        {link.label}
        <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        <span
          className={`absolute -bottom-1.5 left-0 h-px bg-brand-gold transition-all duration-300 ${
            active ? "w-full" : "w-0 group-hover:w-full"
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
          >
            <div
              data-testid={`nav-dropdown-${slug(link.label)}`}
              className="min-w-[240px] bg-brand-cream border border-brand-ink/10 rounded-2xl shadow-[0_20px_50px_rgba(23,17,15,0.08)] p-2"
            >
              {link.children.map((c) => (
                <Link
                  key={c.label}
                  to={c.to}
                  data-testid={`nav-dropdown-link-${slug(c.label)}`}
                  className={`block px-4 py-2.5 rounded-xl text-[13px] tracking-wide transition-colors ${
                    pathname === c.to
                      ? "bg-brand-blush text-brand-ink"
                      : "text-brand-ink/70 hover:bg-brand-blush/60 hover:text-brand-ink"
                  }`}
                >
                  {c.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding,box-shadow] duration-500 ${
        scrolled
          ? "bg-brand-cream/85 backdrop-blur-xl border-b border-brand-ink/10 py-3 shadow-[0_8px_30px_rgba(23,17,15,0.05)]"
          : "bg-transparent py-4"
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="Nervani Beauty" className="h-11 w-11 md:h-12 md:w-12 object-contain" />
          <span className="hidden sm:block leading-none">
            <span className="font-serif text-xl md:text-2xl tracking-tight text-brand-ink uppercase">Nervani</span>
            <span className="block text-[9px] uppercase tracking-[0.3em] text-brand-rose font-semibold mt-0.5">Beauty</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <DesktopLink key={l.label} link={l} pathname={pathname} />
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-btn"
            onClick={openBooking}
            className="hidden md:inline-flex btn-fill bg-brand-ink text-brand-cream px-6 py-2.5 rounded-full text-[13px] tracking-wide font-medium"
          >
            Book Services
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-brand-ink p-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-brand-cream border-t border-brand-ink/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <div key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`nav-mobile-${slug(l.label)}`}
                    className="font-serif text-2xl text-brand-ink"
                  >
                    {l.label}
                  </Link>
                  {Array.isArray(l.children) && (
                    <div className="mt-2 pl-4 flex flex-col gap-2 border-l border-brand-ink/10">
                      {l.children.map((c) => (
                        <Link
                          key={c.label}
                          to={c.to}
                          data-testid={`nav-mobile-${slug(c.label)}`}
                          className="text-brand-ink/70 text-base"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button
                data-testid="nav-mobile-book-btn"
                onClick={openBooking}
                className="mt-2 bg-brand-ink text-brand-cream px-6 py-3 rounded-full text-sm tracking-wide"
              >
                Book Services
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
